import { httpsCallable } from "firebase/functions";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { db, functions, storage } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { downloadCsv } from "../utils/tableTransfer";

type ReplyMode = "none" | "ptu_private" | "all";
type PtuRole = "exec" | "messenger" | "signupCoordinator" | "viewer";

type PtuSettings = {
  messagingEnabled: boolean;
  signupsEnabled: boolean;
  defaultReplyMode: ReplyMode;
  allowGroupReplies: boolean;
  requireReplyModeWarning: boolean;
};

type PtuAuthority = {
  uid: string;
  email: string;
  displayName: string;
  role: PtuRole;
  roleLabel: string;
  status: "active" | "scheduled" | "expired" | "revoked";
  canCreateMessages: boolean;
  canManageSignups: boolean;
  canManagePtu: boolean;
  expiresAt: string | null;
  grantedAt: string | null;
  note?: string;
};

type PtuFamilyAudienceRow = {
  familyId: string;
  familyName: string;
  familyCode: string;
  isActive: boolean;
  parentKeys: string[];
  studentKeys: string[];
  memberUids: string[];
  recipientUids: string[];
  recipientCount: number;
  hasLinkedRecipient: boolean;
};

type PtuAudiencePreview = {
  replyMode: ReplyMode;
  allActive: boolean;
  familyCount: number;
  recipientCount: number;
  responderCount: number;
  unresolvedFamilyCount: number;
  families: PtuFamilyAudienceRow[];
  warnings: string[];
};

type PtuSignupStatus = "draft" | "open" | "closed" | "archived";

type PtuSignup = {
  id: string;
  title: string;
  description: string;
  status: PtuSignupStatus;
  category: string;
  location: string;
  eventDate: string | null;
  openAt: string | null;
  closeAt: string | null;
  allActiveFamilies: boolean;
  familyIds: string[];
  slotCount: number;
  totalQuantity: number;
  claimedQuantity: number;
  remainingQuantity: number;
  volunteerHoursEligible: boolean;
  createdBy: string;
  createdAt: string | null;
  updatedAt: string | null;
};

type PtuSignupClaim = {
  id: string;
  signupId: string;
  slotId: string;
  familyId: string;
  familyName: string;
  quantity: number;
  status: string;
  claimedByEmail: string;
  claimedAt: string | null;
  fulfilledAt: string | null;
  fulfilledByEmail: string;
  volunteerEntryId: string;
  canceledAt: string | null;
};

type PtuSignupSlotDetail = {
  id: string;
  slotId: string;
  title: string;
  description: string;
  quantity: number;
  claimedQuantity: number;
  remainingQuantity: number;
  volunteerHoursPerClaim: number;
  claims: PtuSignupClaim[];
};

type PtuSignupSlotInput = {
  slotId: string;
  title: string;
  description: string;
  quantity: number;
  volunteerHoursPerClaim: number;
};

type PtuSignupDetail = {
  signup: PtuSignup;
  slots: PtuSignupSlotDetail[];
  claims: PtuSignupClaim[];
};

type PtuReport = {
  daysBack: number;
  generatedAt: string;
  summary: {
    messagingEnabled: boolean;
    signupsEnabled: boolean;
    campaignCount: number;
    campaignFamilies: number;
    campaignRecipients: number;
    unresolvedFamilies: number;
    attachmentCount: number;
    byReplyMode: Record<ReplyMode, number>;
    signups: {
      total: number;
      byStatus: Record<PtuSignupStatus, number>;
      totalSlots: number;
      totalQuantity: number;
      claimedQuantity: number;
      remainingQuantity: number;
      volunteerEligible: number;
    };
    recentClaims: {
      active: number;
      canceled: number;
      fulfilled: number;
      quantity: number;
    };
    auditByType: Record<string, number>;
  };
  campaigns: Array<{
    id: string;
    subject: string;
    replyMode: ReplyMode;
    createdAt: string | null;
    createdByEmail: string;
    familyCount: number;
    recipientCount: number;
    responderCount: number;
    unresolvedFamilyCount: number;
    attachmentCount: number;
    threadCount: number;
    schoolYear: string;
  }>;
  auditEvents: Array<{
    id: string;
    type: string;
    createdAt: string | null;
    actorEmail: string;
    actorUid: string;
    meta: Record<string, any>;
  }>;
};

type MessageAttachment = {
  id: string;
  name: string;
  size: number;
  contentType: string;
  storagePath: string;
  downloadUrl: string;
  createdAtIso: string;
};

type AttachmentPolicy = {
  allowedContentTypes: string[];
  maxBytesMessaging: number;
  maxFilesPerMessage: number;
};

const DEFAULT_SETTINGS: PtuSettings = {
  messagingEnabled: false,
  signupsEnabled: false,
  defaultReplyMode: "ptu_private",
  allowGroupReplies: false,
  requireReplyModeWarning: true,
};

function formatDate(value?: string | null) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function replyModeLabel(mode: ReplyMode) {
  if (mode === "none") return "No replies";
  if (mode === "all") return "Group replies";
  return "Private replies to PTU";
}

function normalizeEmail(value: string) {
  return String(value || "").trim().toLowerCase();
}

function makeId() {
  return Math.random().toString(16).slice(2) + "-" + Date.now().toString(16);
}

function safeName(name: string) {
  const cleaned = name.replace(/[^\w.\-() ]+/g, "_").trim();
  return cleaned.length ? cleaned : "file";
}

function inferMimeTypeFromName(name: string): string {
  const lower = name.toLowerCase();
  if (lower.endsWith(".pdf")) return "application/pdf";
  if (lower.endsWith(".docx")) return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
  if (lower.endsWith(".png")) return "image/png";
  if (lower.endsWith(".webp")) return "image/webp";
  return "application/octet-stream";
}

function normalizeMimeType(mimeType: string, fileName: string): string {
  const mt = (mimeType || "").trim().toLowerCase();
  if (mt === "image/jpg") return "image/jpeg";
  if (!mt || mt === "application/octet-stream") return inferMimeTypeFromName(fileName);
  return mt;
}

async function loadAttachmentPolicy(): Promise<AttachmentPolicy> {
  const fallback: AttachmentPolicy = {
    allowedContentTypes: ["application/pdf", "image/jpeg", "image/png", "image/webp", "image/jpg"],
    maxBytesMessaging: 5 * 1024 * 1024,
    maxFilesPerMessage: 3,
  };

  try {
    const snap = await getDoc(doc(db, "settings", "attachmentPolicy"));
    if (!snap.exists()) return fallback;
    const d = snap.data() as any;
    const allowed = Array.isArray(d.allowedContentTypes)
      ? d.allowedContentTypes
      : Array.isArray(d.allowedMimeTypes)
        ? d.allowedMimeTypes
        : fallback.allowedContentTypes;

    return {
      allowedContentTypes: allowed.length ? allowed : fallback.allowedContentTypes,
      maxBytesMessaging: Number(d.maxBytesMessaging ?? d.maxBytes ?? fallback.maxBytesMessaging),
      maxFilesPerMessage: Number(d.maxFilesPerMessage ?? fallback.maxFilesPerMessage),
    };
  } catch {
    return fallback;
  }
}

function newSignupSlotRow(overrides: Partial<PtuSignupSlotInput> = {}): PtuSignupSlotInput {
  return {
    slotId: overrides.slotId || `slot_${makeId()}`,
    title: overrides.title || "",
    description: overrides.description || "",
    quantity: Math.max(1, Number(overrides.quantity || 1) || 1),
    volunteerHoursPerClaim: Math.max(0, Number(overrides.volunteerHoursPerClaim || 0) || 0),
  };
}

function defaultSignupSlots(): PtuSignupSlotInput[] {
  return [
    newSignupSlotRow({ slotId: "slot_1", title: "Setup help", quantity: 4, volunteerHoursPerClaim: 1 }),
    newSignupSlotRow({ slotId: "slot_2", title: "Food contribution", quantity: 12, volunteerHoursPerClaim: 0 }),
    newSignupSlotRow({ slotId: "slot_3", title: "Cleanup help", quantity: 4, volunteerHoursPerClaim: 1 }),
  ];
}

function normalizeSignupSlotsForSave(rows: PtuSignupSlotInput[]) {
  return rows
    .map((slot, index) => ({
      slotId: slot.slotId || `slot_${index + 1}`,
      title: slot.title.trim(),
      quantity: Math.max(1, Number(slot.quantity || 1) || 1),
      volunteerHoursPerClaim: Math.max(0, Number(slot.volunteerHoursPerClaim || 0) || 0),
      description: slot.description.trim(),
    }))
    .filter((slot) => slot.title);
}

function toDateInput(value?: string | null) {
  if (!value) return "";
  try {
    return new Date(value).toISOString().slice(0, 10);
  } catch {
    return String(value).slice(0, 10);
  }
}

function signupSlotRowsFromDetail(slots: PtuSignupSlotDetail[]): PtuSignupSlotInput[] {
  return slots.length
    ? slots.map((slot, index) =>
        newSignupSlotRow({
          slotId: slot.slotId || slot.id || `slot_${index + 1}`,
          title: slot.title,
          description: slot.description || "",
          quantity: slot.quantity,
          volunteerHoursPerClaim: slot.volunteerHoursPerClaim,
        })
      )
    : [newSignupSlotRow({ slotId: "slot_1" })];
}

function signupSlotsSignature(rows: PtuSignupSlotInput[]) {
  return JSON.stringify(normalizeSignupSlotsForSave(rows));
}

export function PtuConsolePage() {
  const { hasPermission } = useAuth();
  const canManageSettings = hasPermission("ptuSettings", "manage");

  const [settings, setSettings] = useState<PtuSettings>(DEFAULT_SETTINGS);
  const [authorities, setAuthorities] = useState<PtuAuthority[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [granting, setGranting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [grantEmail, setGrantEmail] = useState("");
  const [grantRole, setGrantRole] = useState<PtuRole>("messenger");
  const [grantExpiresAt, setGrantExpiresAt] = useState("");
  const [grantNote, setGrantNote] = useState("");
  const [grantSendSetupEmail, setGrantSendSetupEmail] = useState(true);
  const [families, setFamilies] = useState<PtuFamilyAudienceRow[]>([]);
  const [familyFilter, setFamilyFilter] = useState("");
  const [selectedFamilyIds, setSelectedFamilyIds] = useState<string[]>([]);
  const [audienceReplyMode, setAudienceReplyMode] = useState<ReplyMode>("ptu_private");
  const [previewAllActive, setPreviewAllActive] = useState(true);
  const [preview, setPreview] = useState<PtuAudiencePreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [composeSubject, setComposeSubject] = useState("");
  const [composeMessage, setComposeMessage] = useState("");
  const [replyWarningAcknowledged, setReplyWarningAcknowledged] = useState(false);
  const [sendingPtuMessage, setSendingPtuMessage] = useState(false);
  const [attachmentPolicy, setAttachmentPolicy] = useState<AttachmentPolicy | null>(null);
  const [composeAttachments, setComposeAttachments] = useState<MessageAttachment[]>([]);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [signups, setSignups] = useState<PtuSignup[]>([]);
  const [creatingSignup, setCreatingSignup] = useState(false);
  const [signupTitle, setSignupTitle] = useState("");
  const [signupCategory, setSignupCategory] = useState("volunteer");
  const [signupLocation, setSignupLocation] = useState("");
  const [signupEventDate, setSignupEventDate] = useState("");
  const [signupStatus, setSignupStatus] = useState<PtuSignupStatus>("draft");
  const [signupVolunteerHoursEligible, setSignupVolunteerHoursEligible] = useState(true);
  const [signupDescription, setSignupDescription] = useState("");
  const [signupSlots, setSignupSlots] = useState<PtuSignupSlotInput[]>(defaultSignupSlots);
  const [signupDetail, setSignupDetail] = useState<PtuSignupDetail | null>(null);
  const [loadingSignupDetail, setLoadingSignupDetail] = useState(false);
  const [editingSignupId, setEditingSignupId] = useState<string | null>(null);
  const [originalEditSlotsSignature, setOriginalEditSlotsSignature] = useState("");
  const [busyClaimId, setBusyClaimId] = useState<string | null>(null);
  const [reportDaysBack, setReportDaysBack] = useState(30);
  const [report, setReport] = useState<PtuReport | null>(null);
  const [loadingReport, setLoadingReport] = useState(false);

  const activeResponders = useMemo(
    () =>
      authorities.filter(
        (item) =>
          item.status === "active" &&
          (item.canCreateMessages || item.canManagePtu)
      ),
    [authorities]
  );

  const signupSlotsForSave = useMemo(() => normalizeSignupSlotsForSave(signupSlots), [signupSlots]);
  const signupSlotTotals = useMemo(
    () =>
      signupSlotsForSave.reduce(
        (acc, slot) => {
          acc.slotCount += 1;
          acc.totalQuantity += slot.quantity;
          acc.totalHours += slot.quantity * slot.volunteerHoursPerClaim;
          return acc;
        },
        { slotCount: 0, totalQuantity: 0, totalHours: 0 }
      ),
    [signupSlotsForSave]
  );

  async function load() {
    setLoading(true);
    setErr(null);
    setMessage(null);

    try {
      const [settingsRes, authoritiesRes]: any[] = await Promise.all([
        httpsCallable(functions, "getPtuSettings")({}),
        httpsCallable(functions, "listPtuAuthorities")({}),
      ]);
      const nextSettings = { ...DEFAULT_SETTINGS, ...(settingsRes?.data?.settings || {}) };
      setSettings(nextSettings);
      setAudienceReplyMode(nextSettings.defaultReplyMode);
      setAuthorities(Array.isArray(authoritiesRes?.data?.authorities) ? authoritiesRes.data.authorities : []);
      const policy = await loadAttachmentPolicy();
      setAttachmentPolicy(policy);
      await Promise.all([loadFamilies(), loadSignups(), loadReport(reportDaysBack)]);
    } catch (e: any) {
      setErr(e?.message || "Failed to load PTU console.");
    } finally {
      setLoading(false);
    }
  }

  async function loadFamilies() {
    const res: any = await httpsCallable(functions, "listPtuFamilyAudience")({ limit: 1000 });
    setFamilies(Array.isArray(res?.data?.families) ? res.data.families : []);
  }

  async function loadSignups() {
    const res: any = await httpsCallable(functions, "listPtuSignups")({ limit: 100 });
    setSignups(Array.isArray(res?.data?.signups) ? res.data.signups : []);
  }

  async function loadReport(days = reportDaysBack) {
    setLoadingReport(true);
    try {
      const res: any = await httpsCallable(functions, "getPtuReportingDashboard")({ daysBack: days });
      setReport(res?.data || null);
    } catch (e: any) {
      setErr(e?.message || "Failed to load PTU reporting.");
    } finally {
      setLoadingReport(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function saveSettings() {
    setSaving(true);
    setErr(null);
    setMessage(null);

    try {
      const res: any = await httpsCallable(functions, "upsertPtuSettings")(settings);
      setSettings({ ...DEFAULT_SETTINGS, ...(res?.data?.settings || settings) });
      setMessage("PTU settings saved.");
    } catch (e: any) {
      setErr(e?.message || "Failed to save PTU settings.");
    } finally {
      setSaving(false);
    }
  }

  async function grantAuthority() {
    const email = normalizeEmail(grantEmail);
    if (!email) {
      setErr("Enter an email address for the PTU authority grant.");
      return;
    }

    setGranting(true);
    setErr(null);
    setMessage(null);

    try {
      const res: any = await httpsCallable(functions, "grantPtuAuthority")({
        email,
        role: grantRole,
        expiresAt: grantExpiresAt || null,
        note: grantNote,
        sendSetupEmail: grantSendSetupEmail,
      });
      const account = res?.data?.account;
      const accountMessage = account?.existed
        ? " Existing account updated."
        : account?.setupEmailSent
          ? " New account created and setup email sent."
          : account?.setupEmailError
            ? ` New account created, but setup email failed: ${account.setupEmailError}`
            : " New account created; setup email was not sent.";
      setGrantEmail("");
      setGrantExpiresAt("");
      setGrantNote("");
      setMessage(`Granted PTU ${grantRole} authority to ${email}.${accountMessage}`);
      await load();
    } catch (e: any) {
      setErr(e?.message || "Failed to grant PTU authority.");
    } finally {
      setGranting(false);
    }
  }

  function toggleFamily(familyId: string) {
    setSelectedFamilyIds((prev) =>
      prev.includes(familyId) ? prev.filter((id) => id !== familyId) : [...prev, familyId]
    );
  }

  function selectFilteredFamilies() {
    const ids = filteredFamilies.map((family) => family.familyId);
    setSelectedFamilyIds((prev) => Array.from(new Set([...prev, ...ids])));
    setPreviewAllActive(false);
  }

  function clearSelectedFamilies() {
    setSelectedFamilyIds([]);
    setPreviewAllActive(true);
  }

  async function previewAudience() {
    setPreviewing(true);
    setErr(null);
    setMessage(null);
    setPreview(null);

    try {
      const res: any = await httpsCallable(functions, "previewPtuAudience")({
        replyMode: audienceReplyMode,
        allActive: previewAllActive,
        familyIds: selectedFamilyIds,
      });
      setPreview(res?.data || null);
      setReplyWarningAcknowledged(false);
    } catch (e: any) {
      setErr(e?.message || "Failed to preview PTU audience.");
    } finally {
      setPreviewing(false);
    }
  }

  async function sendPtuMessage() {
    if (!settings.messagingEnabled) {
      setErr("PTU message sending is disabled in rollout settings.");
      return;
    }
    if (!preview) {
      setErr("Preview the audience before sending.");
      return;
    }
    if (!replyWarningAcknowledged) {
      setErr("Acknowledge the reply-audience warning before sending.");
      return;
    }
    if (!composeSubject.trim() || !composeMessage.trim()) {
      setErr("Subject and message are required.");
      return;
    }

    const ok = confirm(
      `Send PTU message "${composeSubject.trim()}"?\n\n` +
      `Reply behavior: ${replyModeLabel(audienceReplyMode)}\n` +
      `Families: ${preview.familyCount}\n` +
      `Recipients: ${preview.recipientCount}\n` +
      `PTU responders: ${preview.responderCount}`
    );
    if (!ok) return;

    setSendingPtuMessage(true);
    setErr(null);
    setMessage(null);

    try {
      const res: any = await httpsCallable(functions, "createPtuMessage")({
        subject: composeSubject.trim(),
        message: composeMessage.trim(),
        replyMode: audienceReplyMode,
        allActive: previewAllActive,
        familyIds: selectedFamilyIds,
        replyWarningAcknowledged,
        attachments: composeAttachments,
      });
      const data = res?.data || {};
      setMessage(`PTU message created: ${data.threadCount || 0} thread(s), ${data.recipientCount || 0} recipient(s).`);
      setComposeSubject("");
      setComposeMessage("");
      setComposeAttachments([]);
      setReplyWarningAcknowledged(false);
      setPreview(null);
    } catch (e: any) {
      setErr(e?.message || "Failed to create PTU message.");
    } finally {
      setSendingPtuMessage(false);
    }
  }

  async function uploadComposeAttachments(files: FileList | null) {
    if (!files?.length) return;
    const policy = attachmentPolicy ?? (await loadAttachmentPolicy());
    setAttachmentPolicy(policy);

    if (composeAttachments.length + files.length > policy.maxFilesPerMessage) {
      setErr(`Max ${policy.maxFilesPerMessage} attachment(s) per PTU message.`);
      return;
    }

    setUploadingAttachments(true);
    setErr(null);
    setMessage(null);

    try {
      const uploaded: MessageAttachment[] = [];
      const draftId = makeId();
      for (const file of Array.from(files)) {
        const contentType = normalizeMimeType(file.type, file.name);
        const allowed = policy.allowedContentTypes || [];
        if (allowed.length && !allowed.includes(contentType) && !allowed.includes(file.type || "")) {
          throw new Error(`${file.name} is not an allowed attachment type.`);
        }
        if (file.size > policy.maxBytesMessaging) {
          throw new Error(`${file.name} is larger than ${(policy.maxBytesMessaging / 1024 / 1024).toFixed(1)} MB.`);
        }

        const attId = makeId();
        const storagePath = `ptuMessageAttachments/${draftId}/${attId}-${safeName(file.name)}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file, { contentType });
        const downloadUrl = await getDownloadURL(storageRef);
        uploaded.push({
          id: attId,
          name: file.name,
          size: file.size,
          contentType,
          storagePath,
          downloadUrl,
          createdAtIso: new Date().toISOString(),
        });
      }

      setComposeAttachments((prev) => [...prev, ...uploaded]);
      setMessage(`Uploaded ${uploaded.length} attachment(s).`);
    } catch (e: any) {
      setErr(e?.message || "Failed to upload PTU message attachment.");
    } finally {
      setUploadingAttachments(false);
    }
  }

  async function removeComposeAttachment(att: MessageAttachment) {
    setErr(null);
    setMessage(null);
    try {
      await deleteObject(ref(storage, att.storagePath)).catch(() => undefined);
      setComposeAttachments((prev) => prev.filter((item) => item.id !== att.id));
    } catch (e: any) {
      setErr(e?.message || "Failed to remove PTU message attachment.");
    }
  }

  function updateSignupSlot(slotId: string, patch: Partial<PtuSignupSlotInput>) {
    setSignupSlots((prev) => prev.map((slot) => slot.slotId === slotId ? { ...slot, ...patch } : slot));
  }

  function addSignupSlot(overrides: Partial<PtuSignupSlotInput> = {}) {
    setSignupSlots((prev) => [...prev, newSignupSlotRow(overrides)]);
  }

  function duplicateSignupSlot(slot: PtuSignupSlotInput) {
    addSignupSlot({
      title: `${slot.title || "Signup slot"} copy`,
      description: slot.description,
      quantity: slot.quantity,
      volunteerHoursPerClaim: slot.volunteerHoursPerClaim,
    });
  }

  function removeSignupSlot(slotId: string) {
    setSignupSlots((prev) => prev.length > 1 ? prev.filter((slot) => slot.slotId !== slotId) : [newSignupSlotRow({ slotId: prev[0]?.slotId || "slot_1" })]);
  }

  function applySignupTemplate(template: "potluck" | "event" | "volunteer") {
    const templates: Record<typeof template, PtuSignupSlotInput[]> = {
      potluck: [
        newSignupSlotRow({ title: "Main dish", quantity: 6, volunteerHoursPerClaim: 0 }),
        newSignupSlotRow({ title: "Side dish", quantity: 8, volunteerHoursPerClaim: 0 }),
        newSignupSlotRow({ title: "Dessert", quantity: 6, volunteerHoursPerClaim: 0 }),
        newSignupSlotRow({ title: "Drinks", quantity: 4, volunteerHoursPerClaim: 0 }),
        newSignupSlotRow({ title: "Setup help", quantity: 3, volunteerHoursPerClaim: 1 }),
        newSignupSlotRow({ title: "Cleanup help", quantity: 3, volunteerHoursPerClaim: 1 }),
      ],
      event: [
        newSignupSlotRow({ title: "Setup crew", quantity: 4, volunteerHoursPerClaim: 1 }),
        newSignupSlotRow({ title: "Check-in table", quantity: 2, volunteerHoursPerClaim: 1 }),
        newSignupSlotRow({ title: "Activity helper", quantity: 6, volunteerHoursPerClaim: 1 }),
        newSignupSlotRow({ title: "Cleanup crew", quantity: 4, volunteerHoursPerClaim: 1 }),
      ],
      volunteer: defaultSignupSlots(),
    };
    setSignupSlots(templates[template]);
  }

  async function createSignup() {
    const slots = signupSlotsForSave;
    if (!signupTitle.trim()) {
      setErr("Enter a signup title.");
      return;
    }
    if (!slots.length) {
      setErr("Add at least one signup slot.");
      return;
    }
    if (signupStatus === "open" && !settings.signupsEnabled) {
      setErr("Enable PTU signups before creating an open signup. Drafts are allowed while rollout is disabled.");
      return;
    }

    setCreatingSignup(true);
    setErr(null);
    setMessage(null);

    try {
      const res: any = await httpsCallable(functions, "createPtuSignup")({
        title: signupTitle.trim(),
        category: signupCategory.trim() || "general",
        location: signupLocation.trim(),
        eventDate: signupEventDate || null,
        status: signupStatus,
        volunteerHoursEligible: signupVolunteerHoursEligible,
        description: signupDescription.trim(),
        allActiveFamilies: previewAllActive,
        familyIds: selectedFamilyIds,
        slots,
      });
      const signup = res?.data?.signup;
      setSignups((prev) => signup ? [signup, ...prev.filter((row) => row.id !== signup.id)] : prev);
      resetSignupForm();
      setMessage(`Created PTU signup "${signup?.title || "draft"}".`);
    } catch (e: any) {
      setErr(e?.message || "Failed to create PTU signup.");
    } finally {
      setCreatingSignup(false);
    }
  }

  function resetSignupForm() {
    setEditingSignupId(null);
    setOriginalEditSlotsSignature("");
    setSignupTitle("");
    setSignupCategory("volunteer");
    setSignupLocation("");
    setSignupEventDate("");
    setSignupStatus("draft");
    setSignupVolunteerHoursEligible(true);
    setSignupDescription("");
    setSignupSlots(defaultSignupSlots());
  }

  async function editSignup(signup: PtuSignup) {
    setLoadingSignupDetail(true);
    setErr(null);
    setMessage(null);

    try {
      const res: any = await httpsCallable(functions, "getPtuSignupDetail")({
        signupId: signup.id,
      });
      const detail: PtuSignupDetail = {
        signup: res?.data?.signup || signup,
        slots: Array.isArray(res?.data?.slots) ? res.data.slots : [],
        claims: Array.isArray(res?.data?.claims) ? res.data.claims : [],
      };
      const slotRows = signupSlotRowsFromDetail(detail.slots);
      setEditingSignupId(detail.signup.id);
      setSignupTitle(detail.signup.title);
      setSignupCategory(detail.signup.category || "general");
      setSignupLocation(detail.signup.location || "");
      setSignupEventDate(toDateInput(detail.signup.eventDate));
      setSignupStatus(detail.signup.status);
      setSignupVolunteerHoursEligible(detail.signup.volunteerHoursEligible === true);
      setSignupDescription(detail.signup.description || "");
      setSignupSlots(slotRows);
      setOriginalEditSlotsSignature(signupSlotsSignature(slotRows));
      setPreviewAllActive(detail.signup.allActiveFamilies !== false);
      setSelectedFamilyIds(detail.signup.familyIds || []);
      setSignupDetail(detail);
      setMessage(`Editing "${detail.signup.title}".`);
    } catch (e: any) {
      setErr(e?.message || "Failed to load PTU signup for editing.");
    } finally {
      setLoadingSignupDetail(false);
    }
  }

  async function saveSignupEdit() {
    if (!editingSignupId) return;
    const slots = signupSlotsForSave;
    if (!signupTitle.trim()) {
      setErr("Enter a signup title.");
      return;
    }
    if (!slots.length) {
      setErr("Add at least one signup slot.");
      return;
    }
    if (signupStatus === "open" && !settings.signupsEnabled) {
      setErr("Enable PTU signups before opening this signup.");
      return;
    }

    const replaceSlots = signupSlotsSignature(signupSlots) !== originalEditSlotsSignature;
    setCreatingSignup(true);
    setErr(null);
    setMessage(null);

    try {
      const res: any = await httpsCallable(functions, "updatePtuSignup")({
        signupId: editingSignupId,
        title: signupTitle.trim(),
        category: signupCategory.trim() || "general",
        location: signupLocation.trim(),
        eventDate: signupEventDate || null,
        status: signupStatus,
        volunteerHoursEligible: signupVolunteerHoursEligible,
        description: signupDescription.trim(),
        allActiveFamilies: previewAllActive,
        familyIds: selectedFamilyIds,
        replaceSlots,
        slots: replaceSlots ? slots : undefined,
      });
      const updated = res?.data?.signup;
      setSignups((prev) => prev.map((row) => row.id === editingSignupId ? (updated || row) : row));
      setMessage(`Saved PTU signup "${updated?.title || signupTitle.trim()}".`);
      if (updated) await loadSignupDetail(updated);
      resetSignupForm();
    } catch (e: any) {
      setErr(e?.message || "Failed to save PTU signup.");
    } finally {
      setCreatingSignup(false);
    }
  }

  async function updateSignupStatus(signup: PtuSignup, status: PtuSignupStatus) {
    if (status === "open" && !settings.signupsEnabled) {
      setErr("Enable PTU signups before opening this signup.");
      return;
    }

    setErr(null);
    setMessage(null);
    try {
      const res: any = await httpsCallable(functions, "updatePtuSignupStatus")({
        signupId: signup.id,
        status,
      });
      const updated = res?.data?.signup;
      setSignups((prev) => prev.map((row) => row.id === signup.id ? (updated || row) : row));
      setMessage(`Updated "${signup.title}" to ${status}.`);
    } catch (e: any) {
      setErr(e?.message || "Failed to update PTU signup.");
    }
  }

  async function loadSignupDetail(signup: PtuSignup) {
    setLoadingSignupDetail(true);
    setErr(null);
    setMessage(null);

    try {
      const res: any = await httpsCallable(functions, "getPtuSignupDetail")({
        signupId: signup.id,
      });
      setSignupDetail({
        signup: res?.data?.signup || signup,
        slots: Array.isArray(res?.data?.slots) ? res.data.slots : [],
        claims: Array.isArray(res?.data?.claims) ? res.data.claims : [],
      });
    } catch (e: any) {
      setErr(e?.message || "Failed to load PTU signup claims.");
    } finally {
      setLoadingSignupDetail(false);
    }
  }

  function exportSignupClaimsCsv() {
    if (!signupDetail) return;

    const rows = signupDetail.slots.flatMap((slot) =>
      slot.claims.map((claim) => ({
        Signup: signupDetail.signup.title,
        Slot: slot.title,
        "Slot Description": slot.description || "",
        "Family": claim.familyName || claim.familyId,
        "Family ID": claim.familyId,
        "Claim Status": claim.status,
        Quantity: String(claim.quantity),
        "Claimed By": claim.claimedByEmail || "",
        "Claimed At": claim.claimedAt || "",
        "Fulfilled At": claim.fulfilledAt || "",
        "Fulfilled By": claim.fulfilledByEmail || "",
        "Volunteer Entry ID": claim.volunteerEntryId || "",
        "Canceled At": claim.canceledAt || "",
        "Hours Per Claim": slot.volunteerHoursPerClaim ? String(slot.volunteerHoursPerClaim) : "",
      }))
    );

    const filename = `${signupDetail.signup.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "ptu-signup"}-claims.csv`;
    downloadCsv(filename, rows.length ? rows : [{
      Signup: signupDetail.signup.title,
      Slot: "",
      "Slot Description": "",
      Family: "",
      "Family ID": "",
      "Claim Status": "",
      Quantity: "",
      "Claimed By": "",
      "Claimed At": "",
      "Fulfilled At": "",
      "Fulfilled By": "",
      "Volunteer Entry ID": "",
      "Canceled At": "",
      "Hours Per Claim": "",
    }]);
  }

  async function adminCancelSignupClaim(slot: PtuSignupSlotDetail, claim: PtuSignupClaim) {
    if (!signupDetail) return;
    const ok = confirm(`Cancel ${claim.familyName || claim.familyId}'s claim for "${slot.title}"?`);
    if (!ok) return;

    setErr(null);
    setMessage(null);
    try {
      await httpsCallable(functions, "adminCancelPtuSignupClaim")({
        signupId: signupDetail.signup.id,
        slotId: slot.slotId,
        claimId: claim.id,
      });
      setMessage(`Canceled claim for ${claim.familyName || claim.familyId}.`);
      await Promise.all([loadSignupDetail(signupDetail.signup), loadSignups()]);
    } catch (e: any) {
      setErr(e?.message || "Failed to cancel PTU signup claim.");
    }
  }

  async function fulfillSignupClaim(slot: PtuSignupSlotDetail, claim: PtuSignupClaim) {
    if (!signupDetail) return;
    const hours = signupDetail.signup.volunteerHoursEligible ? slot.volunteerHoursPerClaim * claim.quantity : 0;
    const ok = confirm(
      `Mark ${claim.familyName || claim.familyId} fulfilled for "${slot.title}"?` +
      (hours > 0 ? `\n\nThis will create ${hours.toFixed(2)} approved volunteer hour(s).` : "")
    );
    if (!ok) return;

    setBusyClaimId(claim.id);
    setErr(null);
    setMessage(null);
    try {
      const res: any = await httpsCallable(functions, "fulfillPtuSignupClaim")({
        signupId: signupDetail.signup.id,
        slotId: slot.slotId,
        claimId: claim.id,
        createVolunteerCredit: true,
      });
      const data = res?.data || {};
      setMessage(
        data.volunteerEntryId
          ? `Fulfilled claim and created ${Number(data.approvedHours || 0).toFixed(2)} volunteer hour(s).`
          : "Fulfilled claim."
      );
      await loadSignupDetail(signupDetail.signup);
    } catch (e: any) {
      setErr(e?.message || "Failed to fulfill PTU signup claim.");
    } finally {
      setBusyClaimId(null);
    }
  }

  async function unfulfillSignupClaim(slot: PtuSignupSlotDetail, claim: PtuSignupClaim) {
    if (!signupDetail) return;
    const ok = confirm(`Reverse fulfillment for ${claim.familyName || claim.familyId}'s "${slot.title}" claim?`);
    if (!ok) return;

    setBusyClaimId(claim.id);
    setErr(null);
    setMessage(null);
    try {
      await httpsCallable(functions, "unfulfillPtuSignupClaim")({
        signupId: signupDetail.signup.id,
        slotId: slot.slotId,
        claimId: claim.id,
      });
      setMessage("Reversed PTU claim fulfillment.");
      await loadSignupDetail(signupDetail.signup);
    } catch (e: any) {
      setErr(e?.message || "Failed to reverse PTU signup fulfillment.");
    } finally {
      setBusyClaimId(null);
    }
  }

  const filteredFamilies = useMemo(() => {
    const q = familyFilter.trim().toLowerCase();
    if (!q) return families;
    return families.filter((family) =>
      [
        family.familyName,
        family.familyId,
        family.familyCode,
        ...family.parentKeys,
        ...family.studentKeys,
        ...family.memberUids,
      ].join(" ").toLowerCase().includes(q)
    );
  }, [families, familyFilter]);

  async function revokeAuthority(row: PtuAuthority) {
    const ok = confirm(`Revoke PTU authority for ${row.displayName || row.email || row.uid}?`);
    if (!ok) return;

    setErr(null);
    setMessage(null);
    try {
      await httpsCallable(functions, "revokePtuAuthority")({ targetUid: row.uid });
      setMessage(`Revoked PTU authority for ${row.displayName || row.email || row.uid}.`);
      await load();
    } catch (e: any) {
      setErr(e?.message || "Failed to revoke PTU authority.");
    }
  }

  function exportCampaignReport() {
    const rows = report?.campaigns || [];
    if (!rows.length) {
      setErr("No PTU campaigns are available to export.");
      return;
    }
    downloadCsv(`ptu-campaign-report-${reportDaysBack}-days.csv`, rows.map((row) => ({
      createdAt: row.createdAt || "",
      subject: row.subject,
      replyMode: replyModeLabel(row.replyMode),
      familyCount: String(row.familyCount),
      recipientCount: String(row.recipientCount),
      responderCount: String(row.responderCount),
      unresolvedFamilyCount: String(row.unresolvedFamilyCount),
      attachmentCount: String(row.attachmentCount),
      threadCount: String(row.threadCount),
      createdByEmail: row.createdByEmail,
      schoolYear: row.schoolYear,
      campaignId: row.id,
    })));
  }

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div className="card">
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div>
            <h2 style={{ marginTop: 0 }}>PTU Console</h2>
            <p className="muted" style={{ marginTop: 6 }}>
              Foundation for PTU messaging, signups, responder authority, and safe rollout controls.
            </p>
          </div>
          <button className="btn secondary" onClick={load} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        {err ? <div className="error">{err}</div> : null}
        {message ? <div className="success">{message}</div> : null}
      </div>

      <div className="grid3">
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Messaging</h3>
          <div className={settings.messagingEnabled ? "pill ok" : "pill off"}>
            {settings.messagingEnabled ? "Enabled" : "Disabled"}
          </div>
          <p className="muted">PTU message sending stays off until we intentionally turn it on.</p>
        </div>
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Signups</h3>
          <div className={settings.signupsEnabled ? "pill ok" : "pill off"}>
            {settings.signupsEnabled ? "Enabled" : "Disabled"}
          </div>
          <p className="muted">Signup screens and backend workflows will plug into this flag.</p>
        </div>
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Active Responders</h3>
          <div style={{ fontSize: 30, fontWeight: 900 }}>{activeResponders.length}</div>
          <p className="muted">PTU execs/messengers who can receive private family replies.</p>
        </div>
      </div>

      <div className="card">
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div>
            <h3 style={{ marginTop: 0 }}>Audit & Reporting</h3>
            <p className="muted" style={{ marginTop: 0 }}>
              PTU-only activity snapshot for message campaigns, signup utilization, notifications, and authority changes.
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <select
              className="input"
              value={reportDaysBack}
              onChange={(e) => {
                const days = Number(e.target.value);
                setReportDaysBack(days);
                void loadReport(days);
              }}
              style={{ width: 150 }}
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last year</option>
            </select>
            <button className="btn secondary" onClick={() => loadReport(reportDaysBack)} disabled={loadingReport}>
              {loadingReport ? "Loading..." : "Refresh Report"}
            </button>
            <button className="btn secondary" onClick={exportCampaignReport} disabled={!report?.campaigns?.length}>
              Export Campaigns
            </button>
          </div>
        </div>

        {!report ? (
          <div className="muted">No PTU reporting data loaded yet.</div>
        ) : (
          <>
            <div className="grid4" style={{ marginTop: 12 }}>
              <div className="stat-card">
                <div className="muted">Campaigns</div>
                <div className="stat-number">{report.summary.campaignCount}</div>
                <div className="muted">{report.summary.campaignRecipients} recipients</div>
              </div>
              <div className="stat-card">
                <div className="muted">Unresolved Families</div>
                <div className="stat-number">{report.summary.unresolvedFamilies}</div>
                <div className="muted">No linked app recipient</div>
              </div>
              <div className="stat-card">
                <div className="muted">Signup Claims</div>
                <div className="stat-number">{report.summary.recentClaims.active}</div>
                <div className="muted">{report.summary.recentClaims.fulfilled} fulfilled</div>
              </div>
              <div className="stat-card">
                <div className="muted">Audit Events</div>
                <div className="stat-number">
                  {Object.values(report.summary.auditByType || {}).reduce((sum, count) => sum + Number(count || 0), 0)}
                </div>
                <div className="muted">PTU events in range</div>
              </div>
            </div>

            <div className="grid2" style={{ marginTop: 12 }}>
              <div className="notice-light">
                <b>Reply mix</b>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                  <span className="pill">No replies: {report.summary.byReplyMode.none || 0}</span>
                  <span className="pill">Private: {report.summary.byReplyMode.ptu_private || 0}</span>
                  <span className="pill warn">Group: {report.summary.byReplyMode.all || 0}</span>
                </div>
              </div>
              <div className="notice-light">
                <b>Signup utilization</b>
                <div className="muted" style={{ marginTop: 8 }}>
                  {report.summary.signups.claimedQuantity} of {report.summary.signups.totalQuantity} slots claimed,
                  {" "}{report.summary.signups.remainingQuantity} remaining.
                </div>
              </div>
            </div>

            <div className="grid2" style={{ marginTop: 12 }}>
              <div>
                <h4 style={{ marginBottom: 8 }}>Recent Campaigns</h4>
                {report.campaigns.length === 0 ? (
                  <div className="muted">No PTU message campaigns in this range.</div>
                ) : (
                  <div className="table">
                    <div className="thead" style={{ gridTemplateColumns: "1.5fr 130px 120px 120px" }}>
                      <div>Subject</div>
                      <div>Reply</div>
                      <div>Families</div>
                      <div>When</div>
                    </div>
                    {report.campaigns.slice(0, 8).map((campaign) => (
                      <div className="trow" key={campaign.id} style={{ gridTemplateColumns: "1.5fr 130px 120px 120px" }}>
                        <div>
                          <b>{campaign.subject}</b>
                          {campaign.unresolvedFamilyCount ? (
                            <div className="muted">{campaign.unresolvedFamilyCount} unresolved</div>
                          ) : null}
                        </div>
                        <div>{replyModeLabel(campaign.replyMode)}</div>
                        <div>{campaign.familyCount}</div>
                        <div>{formatDate(campaign.createdAt)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h4 style={{ marginBottom: 8 }}>Recent PTU Audit</h4>
                {report.auditEvents.length === 0 ? (
                  <div className="muted">No PTU audit events in this range.</div>
                ) : (
                  <div className="table">
                    <div className="thead" style={{ gridTemplateColumns: "1.2fr 1fr 120px" }}>
                      <div>Event</div>
                      <div>Actor</div>
                      <div>When</div>
                    </div>
                    {report.auditEvents.slice(0, 8).map((event) => (
                      <div className="trow" key={event.id} style={{ gridTemplateColumns: "1.2fr 1fr 120px" }}>
                        <div><b>{event.type}</b></div>
                        <div>{event.actorEmail || event.actorUid || "system"}</div>
                        <div>{formatDate(event.createdAt)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Rollout Settings</h3>
        <div className="grid2">
          <label style={{ display: "flex", gap: 8, alignItems: "center", opacity: canManageSettings ? 1 : 0.65 }}>
            <input
              type="checkbox"
              checked={settings.messagingEnabled}
              disabled={!canManageSettings}
              onChange={(e) => setSettings((prev) => ({ ...prev, messagingEnabled: e.target.checked }))}
            />
            <span>Enable PTU message sending</span>
          </label>

          <label style={{ display: "flex", gap: 8, alignItems: "center", opacity: canManageSettings ? 1 : 0.65 }}>
            <input
              type="checkbox"
              checked={settings.signupsEnabled}
              disabled={!canManageSettings}
              onChange={(e) => setSettings((prev) => ({ ...prev, signupsEnabled: e.target.checked }))}
            />
            <span>Enable PTU signups</span>
          </label>

          <div>
            <label className="label">Default reply behavior</label>
            <select
              className="input"
              value={settings.defaultReplyMode}
              disabled={!canManageSettings}
              onChange={(e) => setSettings((prev) => ({ ...prev, defaultReplyMode: e.target.value as ReplyMode }))}
            >
              <option value="ptu_private">Private replies to PTU</option>
              <option value="none">No replies</option>
              <option value="all">Group replies</option>
            </select>
          </div>

          <label style={{ display: "flex", gap: 8, alignItems: "center", opacity: canManageSettings ? 1 : 0.65 }}>
            <input
              type="checkbox"
              checked={settings.allowGroupReplies}
              disabled={!canManageSettings}
              onChange={(e) => setSettings((prev) => ({ ...prev, allowGroupReplies: e.target.checked }))}
            />
            <span>Allow group-reply PTU threads</span>
          </label>

          <label style={{ display: "flex", gap: 8, alignItems: "center", opacity: canManageSettings ? 1 : 0.65 }}>
            <input
              type="checkbox"
              checked={settings.requireReplyModeWarning}
              disabled={!canManageSettings}
              onChange={(e) => setSettings((prev) => ({ ...prev, requireReplyModeWarning: e.target.checked }))}
            />
            <span>Require sender reply-audience warning</span>
          </label>
        </div>

        <div className="notice-light" style={{ marginTop: 12 }}>
          Default: {replyModeLabel(settings.defaultReplyMode)}. For production safety, PTU send functionality will remain disabled until the dedicated message workflow is built and tested.
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
          <button className="btn" onClick={saveSettings} disabled={!canManageSettings || loading || saving}>
            {saving ? "Saving..." : "Save PTU Settings"}
          </button>
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Grant PTU Authority</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          Enter a parent or staff email. Existing accounts are updated; new accounts receive a setup link and minimal PTU-only access.
        </p>

        <div className="grid4">
          <div>
            <label className="label">Email</label>
            <input
              className="input"
              value={grantEmail}
              disabled={!canManageSettings}
              onChange={(e) => setGrantEmail(e.target.value)}
              placeholder="person@example.com"
            />
          </div>
          <div>
            <label className="label">Role</label>
            <select
              className="input"
              value={grantRole}
              disabled={!canManageSettings}
              onChange={(e) => setGrantRole(e.target.value as PtuRole)}
            >
              <option value="exec">PTU Exec</option>
              <option value="messenger">Messenger</option>
              <option value="signupCoordinator">Signup Coordinator</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>
          <div>
            <label className="label">Expires</label>
            <input
              className="input"
              type="date"
              value={grantExpiresAt}
              disabled={!canManageSettings}
              onChange={(e) => setGrantExpiresAt(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Note</label>
            <input
              className="input"
              value={grantNote}
              disabled={!canManageSettings}
              onChange={(e) => setGrantNote(e.target.value)}
              placeholder="Fall dinner coordinator"
            />
          </div>
        </div>

        <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12, opacity: canManageSettings ? 1 : 0.65 }}>
          <input
            type="checkbox"
            checked={grantSendSetupEmail}
            disabled={!canManageSettings}
            onChange={(e) => setGrantSendSetupEmail(e.target.checked)}
          />
          <span>Send account setup email for new accounts</span>
        </label>

        <div className="notice-light" style={{ marginTop: 12 }}>
          Leave Expires blank for ongoing PTU board members. Use an expiration date for temporary event helpers.
          Revoking authority drops the account back to PTU viewer access unless another role grants more.
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
          <button className="btn" onClick={grantAuthority} disabled={!canManageSettings || granting || !normalizeEmail(grantEmail)}>
            {granting ? "Granting..." : "Grant Authority"}
          </button>
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Audience Dry Run</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          Preview PTU recipient resolution and reply behavior before we enable live sending.
        </p>

        <div className="grid3">
          <div>
            <label className="label">Reply behavior</label>
            <select
              className="input"
              value={audienceReplyMode}
              onChange={(e) => setAudienceReplyMode(e.target.value as ReplyMode)}
            >
              <option value="ptu_private">Private replies to PTU</option>
              <option value="none">No replies</option>
              <option value="all">Group replies</option>
            </select>
          </div>

          <label style={{ display: "flex", gap: 8, alignItems: "center", paddingTop: 28 }}>
            <input
              type="checkbox"
              checked={previewAllActive}
              onChange={(e) => setPreviewAllActive(e.target.checked)}
            />
            <span>Preview all active families</span>
          </label>

          <div style={{ display: "flex", alignItems: "end", justifyContent: "flex-end" }}>
            <button
              className="btn"
              onClick={previewAudience}
              disabled={previewing || (!previewAllActive && selectedFamilyIds.length === 0)}
            >
              {previewing ? "Previewing..." : "Preview Audience"}
            </button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 12 }}>
          <input
            className="input"
            value={familyFilter}
            onChange={(e) => setFamilyFilter(e.target.value)}
            placeholder="Search families, family IDs, parent IDs, student IDs..."
            style={{ minWidth: 320, flex: 1 }}
          />
          <button className="btn secondary" onClick={selectFilteredFamilies} disabled={!filteredFamilies.length}>
            Select Filtered
          </button>
          <button className="btn secondary" onClick={clearSelectedFamilies}>
            Clear
          </button>
        </div>

        <div className="muted" style={{ marginTop: 10 }}>
          {selectedFamilyIds.length} selected / {filteredFamilies.length} shown / {families.length} active families loaded
        </div>

        {preview ? (
          <div style={{ marginTop: 14 }}>
            <div className="grid4">
              <div className="notice-light">
                <b>Families</b>
                <div style={{ fontSize: 24, fontWeight: 900 }}>{preview.familyCount}</div>
              </div>
              <div className="notice-light">
                <b>Recipients</b>
                <div style={{ fontSize: 24, fontWeight: 900 }}>{preview.recipientCount}</div>
              </div>
              <div className="notice-light">
                <b>PTU Responders</b>
                <div style={{ fontSize: 24, fontWeight: 900 }}>{preview.responderCount}</div>
              </div>
              <div className="notice-light">
                <b>Unresolved</b>
                <div style={{ fontSize: 24, fontWeight: 900 }}>{preview.unresolvedFamilyCount}</div>
              </div>
            </div>

            {preview.warnings.length ? (
              <div className="notice-light" style={{ marginTop: 12, display: "grid", gap: 8 }}>
                {preview.warnings.map((warning, idx) => (
                  <div key={`${warning}-${idx}`}>{warning}</div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        <hr className="hr" />

        <div style={{ maxHeight: 420, overflow: "auto" }}>
          <div className="table">
            <div className="thead" style={{ gridTemplateColumns: "42px 1.4fr 120px 120px 1fr" }}>
              <div></div>
              <div>Family</div>
              <div>Recipients</div>
              <div>Students</div>
              <div>Links</div>
            </div>
            {filteredFamilies.map((family) => {
              const checked = selectedFamilyIds.includes(family.familyId);
              return (
                <div className="trow" key={family.familyId} style={{ gridTemplateColumns: "42px 1.4fr 120px 120px 1fr" }}>
                  <div>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleFamily(family.familyId)}
                    />
                  </div>
                  <div>
                    <b>{family.familyName}</b>
                    <div className="muted" style={{ fontSize: 12 }}>{family.familyId}</div>
                  </div>
                  <div>
                    <span className={family.hasLinkedRecipient ? "pill ok" : "pill off"}>
                      {family.recipientCount}
                    </span>
                  </div>
                  <div>{family.studentKeys.length}</div>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {family.parentKeys.length} parent IDs / {family.memberUids.length} member UIDs
                  </div>
                </div>
              );
            })}
            {filteredFamilies.length === 0 ? (
              <div className="trow" style={{ gridTemplateColumns: "1fr" }}>
                <div className="muted">No matching active families.</div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Compose PTU Message</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          Uses the dry-run audience above. Sending remains blocked until PTU messaging is enabled in rollout settings.
        </p>

        <div className="grid2">
          <div>
            <label className="label">Subject</label>
            <input
              className="input"
              value={composeSubject}
              onChange={(e) => setComposeSubject(e.target.value)}
              placeholder="September PTU potluck, auction volunteers..."
              maxLength={140}
            />
          </div>
          <div>
            <label className="label">Reply behavior</label>
            <input className="input" value={replyModeLabel(audienceReplyMode)} readOnly />
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <label className="label">Message</label>
          <textarea
            className="input"
            rows={8}
            value={composeMessage}
            onChange={(e) => setComposeMessage(e.target.value)}
            placeholder="Write the PTU message families will receive..."
          />
        </div>

        <div className="notice-light" style={{ marginTop: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <div>
              <b>Attachments</b>
              <div className="muted" style={{ fontSize: 12 }}>
                Max {attachmentPolicy?.maxFilesPerMessage ?? 3} file(s), {(((attachmentPolicy?.maxBytesMessaging ?? 5 * 1024 * 1024) / 1024 / 1024)).toFixed(1)} MB each.
              </div>
            </div>
            <label className="btn small secondary" style={{ cursor: uploadingAttachments ? "not-allowed" : "pointer" }}>
              {uploadingAttachments ? "Uploading..." : "Add Files"}
              <input
                type="file"
                multiple
                disabled={uploadingAttachments}
                style={{ display: "none" }}
                onChange={(e) => {
                  void uploadComposeAttachments(e.target.files);
                  e.currentTarget.value = "";
                }}
              />
            </label>
          </div>

          <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
            {composeAttachments.length === 0 ? (
              <div className="muted">No attachments selected.</div>
            ) : composeAttachments.map((att) => (
              <div key={att.id} style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <div>
                  <b>{att.name}</b>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {att.contentType} / {Math.round(att.size / 1024)} KB
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <a className="btn small secondary" href={att.downloadUrl} target="_blank" rel="noreferrer">Open</a>
                  <button className="btn small secondary" onClick={() => void removeComposeAttachment(att)}>
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="notice-light" style={{ marginTop: 12, display: "grid", gap: 8 }}>
          <div><b>Current rollout state:</b> {settings.messagingEnabled ? "PTU sending enabled" : "PTU sending disabled"}</div>
          <div><b>Preview:</b> {preview ? `${preview.familyCount} families / ${preview.recipientCount} recipients / ${preview.responderCount} responders` : "Run Audience Dry Run first"}</div>
          {preview?.warnings?.length ? preview.warnings.map((warning, idx) => (
            <div key={`${warning}-${idx}`}>{warning}</div>
          )) : null}
        </div>

        <label style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 12 }}>
          <input
            type="checkbox"
            checked={replyWarningAcknowledged}
            disabled={!preview}
            onChange={(e) => setReplyWarningAcknowledged(e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span>
            I understand who can see replies for this PTU message.
          </span>
        </label>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 12 }}>
          <button className="btn secondary" onClick={previewAudience} disabled={previewing}>
            {previewing ? "Previewing..." : "Refresh Preview"}
          </button>
          <button
            className="btn"
            onClick={sendPtuMessage}
            disabled={
              sendingPtuMessage ||
              !settings.messagingEnabled ||
              !preview ||
              !replyWarningAcknowledged ||
              !composeSubject.trim() ||
              !composeMessage.trim()
            }
          >
            {sendingPtuMessage ? "Sending..." : "Create PTU Message"}
          </button>
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>{editingSignupId ? "Edit PTU Signup" : "PTU Signups"}</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          Create PTU signup campaigns, manage status, and review family claims by slot.
        </p>

        <div className="grid4">
          <div>
            <label className="label">Title</label>
            <input
              className="input"
              value={signupTitle}
              onChange={(e) => setSignupTitle(e.target.value)}
              placeholder="September potluck"
              maxLength={140}
            />
          </div>
          <div>
            <label className="label">Category</label>
            <select
              className="input"
              value={signupCategory}
              onChange={(e) => setSignupCategory(e.target.value)}
            >
              <option value="volunteer">Volunteer</option>
              <option value="meal">Meal contribution</option>
              <option value="event">Event support</option>
              <option value="general">General</option>
            </select>
          </div>
          <div>
            <label className="label">Event date</label>
            <input
              className="input"
              type="date"
              value={signupEventDate}
              onChange={(e) => setSignupEventDate(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Status</label>
            <select
              className="input"
              value={signupStatus}
              onChange={(e) => setSignupStatus(e.target.value as PtuSignupStatus)}
            >
              <option value="draft">Draft</option>
              <option value="open">Open</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>

        <div className="grid2" style={{ marginTop: 12 }}>
          <div>
            <label className="label">Location</label>
            <input
              className="input"
              value={signupLocation}
              onChange={(e) => setSignupLocation(e.target.value)}
              placeholder="Cafeteria, gym, offsite..."
              maxLength={200}
            />
          </div>
          <label style={{ display: "flex", gap: 8, alignItems: "center", paddingTop: 28 }}>
            <input
              type="checkbox"
              checked={signupVolunteerHoursEligible}
              onChange={(e) => setSignupVolunteerHoursEligible(e.target.checked)}
            />
            <span>Eligible for volunteer hours</span>
          </label>
        </div>

        <div style={{ marginTop: 12 }}>
          <label className="label">Description</label>
          <textarea
            className="input"
            rows={4}
            value={signupDescription}
            onChange={(e) => setSignupDescription(e.target.value)}
            placeholder="Optional notes for families..."
          />
        </div>

        <div style={{ marginTop: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <div>
              <label className="label">Signup Slots</label>
              <div className="muted" style={{ fontSize: 12 }}>
                Add one row for each item or job families can claim.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <button type="button" className="btn small secondary" onClick={() => applySignupTemplate("potluck")}>
                Potluck Template
              </button>
              <button type="button" className="btn small secondary" onClick={() => applySignupTemplate("event")}>
                Event Template
              </button>
              <button type="button" className="btn small secondary" onClick={() => addSignupSlot()}>
                Add Slot
              </button>
            </div>
          </div>

          <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
            {signupSlots.map((slot, index) => (
              <div
                key={slot.slotId}
                style={{
                  border: "1px solid var(--border)",
                  borderRadius: 10,
                  padding: 12,
                  background: "var(--surface)",
                  display: "grid",
                  gap: 10,
                }}
              >
                <div className="grid4" style={{ alignItems: "end" }}>
                  <div>
                    <label className="label">Slot {index + 1}</label>
                    <input
                      className="input"
                      value={slot.title}
                      onChange={(e) => updateSignupSlot(slot.slotId, { title: e.target.value })}
                      placeholder="Setup help, main dish, drinks..."
                      maxLength={120}
                    />
                  </div>
                  <div>
                    <label className="label">Spots Needed</label>
                    <input
                      className="input"
                      type="number"
                      min={1}
                      max={250}
                      value={slot.quantity}
                      onChange={(e) => updateSignupSlot(slot.slotId, { quantity: Math.max(1, Number(e.target.value || 1) || 1) })}
                    />
                  </div>
                  <div>
                    <label className="label">Hours Per Claim</label>
                    <input
                      className="input"
                      type="number"
                      min={0}
                      max={24}
                      step={0.25}
                      value={slot.volunteerHoursPerClaim}
                      onChange={(e) => updateSignupSlot(slot.slotId, { volunteerHoursPerClaim: Math.max(0, Number(e.target.value || 0) || 0) })}
                      disabled={!signupVolunteerHoursEligible}
                    />
                  </div>
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
                    <button type="button" className="btn small secondary" onClick={() => duplicateSignupSlot(slot)}>
                      Duplicate
                    </button>
                    <button type="button" className="btn small secondary" onClick={() => removeSignupSlot(slot.slotId)}>
                      Remove
                    </button>
                  </div>
                </div>
                <div>
                  <label className="label">Family-Facing Note</label>
                  <input
                    className="input"
                    value={slot.description}
                    onChange={(e) => updateSignupSlot(slot.slotId, { description: e.target.value })}
                    placeholder="Optional: arrival time, serving size, supplies to bring..."
                    maxLength={240}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="notice-light" style={{ marginTop: 10 }}>
            {signupSlotTotals.slotCount} slot type{signupSlotTotals.slotCount === 1 ? "" : "s"} / {signupSlotTotals.totalQuantity} total spots
            {signupVolunteerHoursEligible ? ` / ${signupSlotTotals.totalHours.toFixed(2).replace(/\.00$/, "")} possible volunteer hours` : " / volunteer hour credit off"}
          </div>
        </div>

        <div className="notice-light" style={{ marginTop: 12 }}>
          Audience uses the dry-run controls above: {previewAllActive ? "all active families" : `${selectedFamilyIds.length} selected families`}. Open signups are blocked until PTU signups are enabled.
          {editingSignupId ? " Slot changes are blocked if this signup has active claims." : ""}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 12 }}>
          {editingSignupId ? (
            <button className="btn secondary" onClick={resetSignupForm} disabled={creatingSignup}>
              Cancel Edit
            </button>
          ) : null}
          <button
            className="btn"
            onClick={editingSignupId ? saveSignupEdit : createSignup}
            disabled={creatingSignup || !signupTitle.trim() || !signupSlotsForSave.length}
          >
            {creatingSignup ? "Saving..." : editingSignupId ? "Save PTU Signup" : "Create PTU Signup"}
          </button>
        </div>

        <hr className="hr" />

        {signups.length === 0 ? (
          <div className="muted">No PTU signups yet.</div>
        ) : (
          <div className="table">
            <div className="thead" style={{ gridTemplateColumns: "1.5fr 110px 120px 150px 130px 260px" }}>
              <div>Signup</div>
              <div>Status</div>
              <div>Slots</div>
              <div>Claims</div>
              <div>Event</div>
              <div></div>
            </div>
            {signups.map((signup) => (
              <div className="trow" key={signup.id} style={{ gridTemplateColumns: "1.5fr 110px 120px 150px 130px 260px" }}>
                <div>
                  <b>{signup.title}</b>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {signup.category || "general"}{signup.location ? ` / ${signup.location}` : ""}
                  </div>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {signup.allActiveFamilies ? "All active families" : `${signup.familyIds.length} selected families`}
                  </div>
                </div>
                <div><span className={signup.status === "open" ? "pill ok" : "pill off"}>{signup.status}</span></div>
                <div>{signup.slotCount} / {signup.totalQuantity} spots</div>
                <div>{signup.claimedQuantity} claimed / {signup.remainingQuantity} open</div>
                <div>{signup.eventDate ? new Date(signup.eventDate).toLocaleDateString() : "-"}</div>
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
                  <button className="btn small secondary" onClick={() => editSignup(signup)} disabled={loadingSignupDetail}>
                    Edit
                  </button>
                  <button className="btn small secondary" onClick={() => loadSignupDetail(signup)} disabled={loadingSignupDetail}>
                    Claims
                  </button>
                  <button className="btn small secondary" onClick={() => updateSignupStatus(signup, "open")} disabled={signup.status === "open" || !settings.signupsEnabled}>
                    Open
                  </button>
                  <button className="btn small secondary" onClick={() => updateSignupStatus(signup, "closed")} disabled={signup.status === "closed"}>
                    Close
                  </button>
                  <button className="btn small secondary" onClick={() => updateSignupStatus(signup, "archived")} disabled={signup.status === "archived"}>
                    Archive
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {signupDetail ? (
          <div className="notice-light" style={{ marginTop: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
              <div>
                <h4 style={{ margin: "0 0 4px" }}>{signupDetail.signup.title}</h4>
                <div className="muted">
                  {signupDetail.claims.filter((claim) => claim.status === "active").length} active claim(s), {signupDetail.claims.length} total claim record(s)
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button className="btn small secondary" onClick={exportSignupClaimsCsv}>Export CSV</button>
                <button className="btn small secondary" onClick={() => setSignupDetail(null)}>Close</button>
              </div>
            </div>

            <div className="table" style={{ marginTop: 12 }}>
              <div className="thead" style={{ gridTemplateColumns: "1.2fr 120px 1.5fr 130px" }}>
                <div>Slot</div>
                <div>Capacity</div>
                <div>Claims</div>
                <div>Hours</div>
              </div>
              {signupDetail.slots.map((slot) => (
                <div className="trow" key={slot.id} style={{ gridTemplateColumns: "1.2fr 120px 1.5fr 130px", alignItems: "start" }}>
                  <div>
                    <b>{slot.title}</b>
                    {slot.description ? <div className="muted" style={{ fontSize: 12 }}>{slot.description}</div> : null}
                  </div>
                  <div>
                    {slot.claimedQuantity} / {slot.quantity}
                    <div className="muted" style={{ fontSize: 12 }}>{slot.remainingQuantity} open</div>
                  </div>
                  <div style={{ display: "grid", gap: 6 }}>
                    {slot.claims.length === 0 ? (
                      <span className="muted">No claims yet.</span>
                    ) : slot.claims.map((claim) => (
                      <div key={claim.id}>
                        <b>{claim.familyName || claim.familyId}</b>
                        <span className={claim.status === "active" ? "pill ok" : "pill off"} style={{ marginLeft: 8 }}>
                          {claim.status}
                        </span>
                        <div className="muted" style={{ fontSize: 12 }}>
                          Qty {claim.quantity} / {claim.claimedByEmail || "unknown"} / {formatDate(claim.claimedAt)}
                          {claim.canceledAt ? ` / canceled ${formatDate(claim.canceledAt)}` : ""}
                        </div>
                        {claim.fulfilledAt ? (
                          <div className="muted" style={{ fontSize: 12 }}>
                            Fulfilled {formatDate(claim.fulfilledAt)}
                            {claim.volunteerEntryId ? ` / volunteer entry ${claim.volunteerEntryId}` : ""}
                          </div>
                        ) : null}
                        {claim.status === "active" ? (
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
                            {claim.fulfilledAt ? (
                              <button
                                className="btn small secondary"
                                onClick={() => unfulfillSignupClaim(slot, claim)}
                                disabled={busyClaimId === claim.id}
                              >
                                Reverse Fulfillment
                              </button>
                            ) : (
                              <button
                                className="btn small secondary"
                                onClick={() => fulfillSignupClaim(slot, claim)}
                                disabled={busyClaimId === claim.id}
                              >
                                Mark Fulfilled
                              </button>
                            )}
                            <button
                              className="btn small secondary"
                              onClick={() => adminCancelSignupClaim(slot, claim)}
                              disabled={busyClaimId === claim.id}
                            >
                              Cancel Claim
                            </button>
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                  <div>{slot.volunteerHoursPerClaim ? `${slot.volunteerHoursPerClaim} per claim` : "-"}</div>
                </div>
              ))}
              {signupDetail.slots.length === 0 ? (
                <div className="trow" style={{ gridTemplateColumns: "1fr" }}>
                  <div className="muted">No slots found for this signup.</div>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>PTU Authority</h3>
        {loading ? (
          <div className="muted">Loading...</div>
        ) : authorities.length === 0 ? (
          <div className="muted">No PTU authority records yet.</div>
        ) : (
          <div className="table">
            <div className="thead" style={{ gridTemplateColumns: "1.4fr 130px 110px 1fr 150px" }}>
              <div>Person</div>
              <div>Role</div>
              <div>Status</div>
              <div>Capabilities</div>
              <div></div>
            </div>
            {authorities.map((row) => (
              <div className="trow" key={row.uid} style={{ gridTemplateColumns: "1.4fr 130px 110px 1fr 150px" }}>
                <div>
                  <b>{row.displayName || row.email || row.uid}</b>
                  <div className="muted" style={{ fontSize: 12 }}>{row.email || row.uid}</div>
                  <div className="muted" style={{ fontSize: 12 }}>
                    Granted {formatDate(row.grantedAt)} / Expires {formatDate(row.expiresAt)}
                  </div>
                  {row.note ? <div className="muted" style={{ fontSize: 12 }}>{row.note}</div> : null}
                </div>
                <div>{row.roleLabel || row.role}</div>
                <div><span className={row.status === "active" ? "pill ok" : "pill off"}>{row.status}</span></div>
                <div className="muted" style={{ fontSize: 12 }}>
                  {[
                    row.canCreateMessages ? "create messages" : "",
                    row.canManageSignups ? "manage signups" : "",
                    row.canManagePtu ? "manage PTU" : "",
                  ].filter(Boolean).join(", ") || "viewer"}
                </div>
                <div style={{ textAlign: "right" }}>
                  <button
                    className="btn small secondary"
                    disabled={!canManageSettings || row.status === "revoked"}
                    onClick={() => revokeAuthority(row)}
                  >
                    Revoke
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
